import { createFileRoute } from '@tanstack/react-router'

import { useCreateCustomer } from '@/api/api-mutations'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { toast, useCreatedToast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'

export const Route = createFileRoute('/_authenticated/_general/customers/create')({
	component: RouteComponent,
	staticData: {
		crumb: ({ context: { L } }) => L.Customers.CreateTitle()
	}
})

function RouteComponent() {
	return (
		<RouteModal asDrawer>
			<NewCustomerForm />
		</RouteModal>
	)
}

function NewCustomerForm() {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()
	const createdToast = useCreatedToast()

	const createCustomer = useCreateCustomer()

	const form = useAppForm({
		defaultValues: {
			firstName: '',
			lastName: '',
			email: '',
			phone: '',
			ssn: '',
			externalId: ''
		},
		onSubmit: async ({ value }) => {
			const body: Record<string, string | undefined> = {}
			if (value.firstName) body.firstName = value.firstName
			if (value.lastName) body.lastName = value.lastName
			if (value.email) body.email = value.email
			if (value.phone) body.phone = value.phone
			if (value.ssn) body.ssn = value.ssn
			if (value.externalId) body.externalId = value.externalId

			const name = [value.firstName, value.lastName].filter(Boolean).join(' ')
			const result = await createCustomer({ body })
			if (result.isSuccess) {
				createdToast(L.Customers.CreateSuccess({ name: name || L.Customers.Anonymous() }), {
					to: '/customers/$customerUuid',
					params: { customerUuid: result.response.uuid }
				})
				handleSuccess()
			} else {
				toast.error(L.Error.SomethingWentWrong())
			}
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Customers.CreateTitle()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField
							name='firstName'
							validators={{ onChange: ({ value }) => (!value ? L.General.Required() : undefined) }}
							children={field => <field.TextField autoFocus label={L.Customers.Field.FirstName()} />}
						/>
						<form.AppField
							name='lastName'
							validators={{ onChange: ({ value }) => (!value ? L.General.Required() : undefined) }}
							children={field => <field.TextField label={L.Customers.Field.LastName()} />}
						/>
						<form.AppField
							name='email'
							children={field => <field.TextField label={L.Fields.Email()} optional />}
						/>
						<form.AppField
							name='phone'
							children={field => <field.TextField label={L.Customers.Field.Phone()} optional />}
						/>
						<form.AppField
							name='ssn'
							children={field => <field.TextField label={L.Customers.Field.SSN()} optional />}
						/>
						<form.AppField
							name='externalId'
							children={field => <field.TextField label={L.Customers.Column.ExternalId()} optional />}
						/>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.CreateButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
